"use client";

import ListCard from "@/components/views/list/card/VenueCard";
import apiFetch from "@/lib/fetch";
import { useEffect, useState, useRef } from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import VenueCardSkeleton from "@/components/views/list/card/VenueCardSkeleton";
import { BlogListType } from "@/@core/enums/blog.enum";
import { useUserLocation } from "@/context/UserLocationContext";

export default function BlogRelatedVenuesInfiniteScroll({
  data,
  blogSlug,
  listType,
}: {
  data: any;
  blogSlug: string;
  listType: BlogListType;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [venues, setVenues] = useState<any[]>(data ? data : []);
  const [hasMore, setHasMore] = useState(true);

  const {
    locationPermission,
    userLocation,
    venueDistances,
    calculateDistances,
  } = useUserLocation();

  const calculatedVenueIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (data.length < DEFAULT_DATA_LIMIT) {
      setHasMore(false);
    }
    setIsLoading(false);
  }, [data]);

  // Calculate distances when user location is available
  useEffect(() => {
    if (locationPermission === "granted" && userLocation && venues.length > 0) {
      const venuesToCalculate = venues
        .filter((venue: any) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: any) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        venuesToCalculate.forEach((v: { id: number }) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, venues, calculateDistances]);

  const fetchData = async (index = 0) => {
    setIsLoading(true);
    const res = await apiFetch({
      route: `venue/related-by-blog/${blogSlug}?index=${index}`,
    });
    if (res.length < DEFAULT_DATA_LIMIT) {
      setHasMore(false);
    }
    setIsLoading(false);

    return res;
  };

  const next = (page: number) => {
    fetchData(page).then((data) => {
      setVenues([...venues, ...data]);
    });
  };

  if (!Array.isArray(venues) || !venues) return <></>;

  return (
    <InfiniteScroll
      hasMore={hasMore}
      next={next}
      loading={<VenueLoading count={6} />}
      isLoading={isLoading}
      skeletonElementId="blog-venue-card-skeleton"
      dataLength={venues?.length || 0}
    >
      <div className="xl:grid justify-items-center grid-cols-1 flex flex-wrap justify-center xl:grid-cols-2 gap-5 py-4">
        {Array.isArray(venues) &&
          venues?.map((venue: any, index: number) => {
            const distance = venueDistances.get(venue.id);
            return (
              <ListCard
                firstPriority={
                  index == 0 && listType == BlogListType.FIRST_VENUE
                }
                titleHeadingLevel="h3"
                key={index}
                distance={distance}
                {...venue}
                index={index}
              />
            );
          })}
      </div>
    </InfiniteScroll>
  );
}

export function VenueLoading({ count }: { count: number }) {
  return (
    <div
      id="blog-venue-card-skeleton"
      className="xl:grid justify-items-center grid-cols-1 flex flex-wrap justify-center xl:grid-cols-2 gap-5 py-4"
    >
      {Array(count)
        .fill(0)
        .map((_, index) => {
          return <VenueCardSkeleton key={index} />;
        })}
    </div>
  );
}
